import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuthContext } from '../../../contexts/AuthContext';

const MAX_ENROLL_ATTEMPTS = 5;

const TwoFactorSetup = () => {
  const {
    isTotpEnrolled,
    startTotpEnrollment,
    finalizeTotpEnrollment,
    unenrollTotp,
  } = useAuthContext();

  const [step, setStep] = useState('idle'); // idle | qr | disabling | confirm-disable
  const [totpUri, setTotpUri] = useState('');
  const [secretObj, setSecretObj] = useState(null);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [enrollAttempts, setEnrollAttempts] = useState(0);
  const inputRefs = useRef([]);
  const cancelledRef = useRef(false);

  const enrolled = isTotpEnrolled();

  // -- Enable flow --

  const handleStartEnrollment = async () => {
    setIsLoading(true);
    setError('');
    setEnrollAttempts(0);
    cancelledRef.current = false;
    try {
      const { secret, totpUri: uri } = await startTotpEnrollment();
      setSecretObj(secret);
      setTotpUri(uri);
      setStep('qr');
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setError('For security, please log out and log back in before enabling 2FA.');
      } else {
        setError('Failed to generate QR code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newCode = [...code];
    for (let i = 0; i < 6; i++) newCode[i] = pasted[i] || '';
    setCode(newCode);
    const lastIndex = Math.min(pasted.length, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleVerifyAndEnroll = async (e) => {
    e.preventDefault();
    const otpCode = code.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the full 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await finalizeTotpEnrollment(secretObj, otpCode);
      setMessage('Two-factor authentication enabled successfully!');
      setStep('idle');
      setCode(['', '', '', '', '', '']);
      setSecretObj(null);
      setTotpUri('');
      setEnrollAttempts(0);
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      const newAttempts = enrollAttempts + 1;
      setEnrollAttempts(newAttempts);

      if (newAttempts >= MAX_ENROLL_ATTEMPTS) {
        // Too many failures — rotate secret by restarting enrollment
        setError('Too many failed attempts. Please scan the new QR code and try again.');
        setCode(['', '', '', '', '', '']);
        setSecretObj(null);
        setTotpUri('');
        setEnrollAttempts(0);
        // Auto-restart enrollment with a fresh secret
        try {
          const { secret, totpUri: uri } = await startTotpEnrollment();
          // Guard against stale write if user cancelled during rotation
          if (!cancelledRef.current) {
            setSecretObj(secret);
            setTotpUri(uri);
          }
        } catch (restartErr) {
          if (!cancelledRef.current) {
            setStep('idle');
            setError('Failed to regenerate QR code. Please try again.');
          }
        }
        return;
      }

      if (err.code === 'auth/invalid-verification-code') {
        setError(`Invalid code. ${MAX_ENROLL_ATTEMPTS - newAttempts} attempt(s) remaining.`);
      } else {
        setError('Verification failed. Please try again.');
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEnrollment = () => {
    cancelledRef.current = true;
    setStep('idle');
    setCode(['', '', '', '', '', '']);
    setSecretObj(null);
    setTotpUri('');
    setError('');
    setEnrollAttempts(0);
    setIsLoading(false);
  };

  // -- Disable flow --

  const handleDisableRequest = () => {
    setStep('confirm-disable');
    setError('');
  };

  const handleConfirmDisable = async () => {
    setStep('disabling');
    setIsLoading(true);
    setError('');
    try {
      await unenrollTotp();
      setMessage('Two-factor authentication disabled.');
      setStep('idle');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setError('For security, please log out and log back in before disabling 2FA.');
      } else {
        setError('Failed to disable 2FA. Please try again.');
      }
      setStep('idle');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDisable = () => {
    setStep('idle');
    setError('');
  };

  // -- Render --

  return (
    <div className="bg-gray-900 rounded-lg p-6">
      <h2 className="text-white text-xl font-semibold mb-2">Security</h2>
      <p className="text-gray-400 text-sm mb-6">
        Add an extra layer of security to your account with two-factor authentication.
      </p>

      {message && (
        <div className="bg-green-500/10 border border-green-500 text-green-400 rounded-lg p-3 text-sm mb-4">
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 rounded-lg p-3 text-sm mb-4">
          {error}
        </div>
      )}

      {/* -- Idle state -- */}
      {step === 'idle' && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium">Authenticator App (TOTP)</h3>
            <p className="text-gray-400 text-sm">
              {enrolled
                ? 'Two-factor authentication is enabled'
                : 'Use Google Authenticator, Authy, or similar apps'}
            </p>
          </div>
          {enrolled ? (
            <button
              onClick={handleDisableRequest}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg border border-red-500 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50"
            >
              Disable
            </button>
          ) : (
            <button
              onClick={handleStartEnrollment}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Enable'}
            </button>
          )}
        </div>
      )}

      {/* -- Confirm disable -- */}
      {step === 'confirm-disable' && (
        <div className="space-y-4">
          <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-400 rounded-lg p-4 text-sm">
            <p className="font-medium mb-1">Are you sure you want to disable 2FA?</p>
            <p>This will remove the extra layer of security from your account. You can re-enable it anytime.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleCancelDisable}
              className="px-4 py-2 text-sm rounded-lg border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDisable}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Disabling...' : 'Yes, Disable 2FA'}
            </button>
          </div>
        </div>
      )}

      {/* -- QR code step -- */}
      {step === 'qr' && (
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-gray-300 text-sm mb-4">
              Scan this QR code with your authenticator app:
            </p>
            <div className="bg-white p-4 rounded-lg inline-block">
              <QRCodeSVG value={totpUri} size={200} />
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-400 text-sm mb-2">
              Then enter the 6-digit verification code:
            </p>
          </div>

          <form onSubmit={handleVerifyAndEnroll}>
            <div className="flex justify-center gap-3 mb-4">
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  onPaste={handleCodePaste}
                  className="w-12 h-14 text-center text-2xl font-mono bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none"
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={handleCancelEnrollment}
                className="px-6 py-2 text-sm rounded-lg border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || code.some((d) => d === '')}
                className="px-6 py-2 text-sm rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* -- Disabling state -- */}
      {step === 'disabling' && (
        <div className="text-center py-4">
          <p className="text-gray-400">Disabling two-factor authentication...</p>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetup;
